import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { getHtmlLanguage } from '../i18n/language';
import { isPrerender } from '../utils/isPrerender';
import { DEFAULT_LOCALE, getPathLocale, isPublicContentPath, localizeHref, LOCALE_INFO, pathForLocale, SITE_LOCALES, SITE_ORIGIN, stripPathLocale } from '../i18n/localizedRoutes';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  ogImage?: string;
  ogImageAlt?: string;
  canonical?: string;
  noindex?: boolean;
}

const SEO: React.FC<SEOProps> = ({
  title,
  description,
  keywords,
  ogImage = 'https://deepsoulai.com/deepsoul_logo.png',
  ogImageAlt,
  canonical,
  noindex = false,
}) => {
  const { t, i18n } = useTranslation();
  const { pathname } = useLocation();
  const locale = getPathLocale(pathname);
  const canonicalUrl = new URL(canonical || pathname, SITE_ORIGIN);
  const resolvedCanonical = SITE_ORIGIN + (locale
    ? localizeHref(canonicalUrl.pathname, locale)
    : canonicalUrl.pathname);
  const resolvedImage = new URL(ogImage || '/deepsoul_logo.png', 'https://deepsoulai.com/').href;
  const resolvedTitle = title || t('seo.default.title');
  const resolvedDescription = description || t('seo.default.description');
  const resolvedKeywords = keywords || t('seo.default.keywords');
  const htmlLanguage = locale ? LOCALE_INFO[locale].htmlLang : getHtmlLanguage(i18n.resolvedLanguage || i18n.language);
  const basePath = stripPathLocale(pathname);
  const showAlternates = !!locale && !noindex && isPublicContentPath(basePath);

  // React 19 hoists metadata but does not replace the static HTML fallback tags.
  useEffect(() => {
    document.querySelectorAll("[data-seo-fallback]").forEach((node) => node.remove());
  }, []);

  useEffect(() => {
    if (isPrerender) {
      document.documentElement.dataset.prerenderReady = resolvedCanonical;
    }
  }, [resolvedCanonical, resolvedTitle, resolvedDescription]);

  return (
    <Helmet>
      <html lang={htmlLanguage} />
      <title>{resolvedTitle}</title>
      <meta name="description" content={resolvedDescription} />
      <meta name="keywords" content={resolvedKeywords} />
      <link rel="canonical" href={resolvedCanonical} />
      {showAlternates && SITE_LOCALES.map(alternate => (
        <link key={alternate} rel="alternate" hrefLang={LOCALE_INFO[alternate].hreflang}
          href={SITE_ORIGIN + pathForLocale(basePath, alternate)} />
      ))}
      {showAlternates && <link rel="alternate" hrefLang="x-default"
        href={SITE_ORIGIN + pathForLocale(basePath, DEFAULT_LOCALE)} />}
      
      <meta name="robots" content={noindex ? "noindex,follow" : "index,follow"} />
      
      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Deep Soul" />
      <meta property="og:locale" content={locale ? LOCALE_INFO[locale].ogLocale : htmlLanguage === "en" ? "en_US" : htmlLanguage.replace("-", "_")} />
      <meta property="og:title" content={resolvedTitle} />
      <meta property="og:description" content={resolvedDescription} />
      <meta property="og:image" content={resolvedImage} />
      {ogImageAlt && <meta property="og:image:alt" content={ogImageAlt} />}
      <meta property="og:url" content={resolvedCanonical} />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={resolvedCanonical} />
      <meta name="twitter:title" content={resolvedTitle} />
      <meta name="twitter:description" content={resolvedDescription} />
      <meta name="twitter:image" content={resolvedImage} />
      {ogImageAlt && <meta name="twitter:image:alt" content={ogImageAlt} />}
    </Helmet>
  );
};

export default SEO;

